import {ICONS} from "patient-ping-remedy/packages/icon";
import {ADTEvent} from "@/api/dto/client-profile";
import React from "react";
import {
  ADMITTED, CLOSED, DECEASED, DISCHARGED, ENDED_SERVICES,
  MEDICAL_LEAVE_OF_ABSENCE, NO_CURRENT_STATUS,
  PAYOR_CHANGED, PRESENTED, RESUMED_SERVICES, TRANSFERRED, VISIT
} from "@manual_encounters/constants/PingStatuses";
import {CareJourneyEventType} from "@/api/dto/care-journey";

export type ADTStatus = {
  displayName: string;
  iconClass: ICONS;
  displayMessage: (event: ADTEvent, facilityName: string, dischargeDispo: string) => React.JSX.Element;
  display: boolean;
}

export type StatusMap = {
  [key: string]: ADTStatus
}

// these statuses map directly to statuses returned from pings
export const Status : StatusMap = {
  // unknown if pings does not return a status
  'Unknown': {
    displayName: 'Unknown',
    iconClass: ICONS["trash-alt"],
    displayMessage: (_: ADTEvent, facilityName: string) => {
      return <><span className="event-text-bold">Unknown</span> at {facilityName}</>;
    },
    display: false
  },
  [ADMITTED]: {
    displayName: 'Admitted',
    iconClass: ICONS.bed,
    displayMessage: (event: ADTEvent, facilityName: string) => {
      return <><span className="event-text-bold">{event.status}</span> to {facilityName}
        {event.setting && <span className="pipe-separator">{event.setting}</span>}</>;
    },
    display: true
  },
  [DECEASED]: {
    displayName: 'Deceased',
    iconClass: ICONS.tombstone,
    displayMessage: (event: ADTEvent, facilityName: string) => {
      return <><span className="event-text-bold">{event.status}</span> at {facilityName}</>;
    },
    display: true
  },
  [DISCHARGED]: {
    displayName: 'Discharged',
    iconClass: ICONS["walking"],
    displayMessage: (event: ADTEvent, facilityName: string, dischargeDispo: string) => {
      return <><span className="event-text-bold">{event.status}</span> from {facilityName}
        {dischargeDispo && <span>{" to " + dischargeDispo}</span>}
      </>;
    },
    display: true
  },
  [PAYOR_CHANGED]: {
    displayName: 'Payor Change',
    iconClass: ICONS["trash-alt"],
    displayMessage: (event: ADTEvent) => {
      return <><span className="event-text-bold">{event.status}</span> to {event.payor?.insuranceCompanyName}</>;
    },
    display: false // at some point product wanted to hide this event
  },
  [PRESENTED]: {
    displayName: 'Presented',
    iconClass: ICONS["stretcher"],
    displayMessage: (event: ADTEvent, facilityName: string) => {
      return <><span className="event-text-bold">{event.status}</span> to {facilityName}
        {event.setting && <span className="pipe-separator">{event.setting}</span>}</>;
    },
    display: true
  },
  [TRANSFERRED]: {
    displayName: 'Transferred',
    iconClass: ICONS.ambulance,
    displayMessage: (event: ADTEvent, facilityName: string) => {
      return <><span className="event-text-bold">{event.status}</span> to {facilityName}
        {event.setting && <span className="pipe-separator">{event.setting}</span>}</>;
    },
    display: true
  },
  [MEDICAL_LEAVE_OF_ABSENCE]: {
    displayName: 'Medical LOA',
    iconClass: ICONS["trash-alt"],
    displayMessage: (event: ADTEvent, facilityName: string) => {
      return <><span className="event-text-bold">{event.status}</span> at {facilityName}</>;
    },
    display: false  // not in v1 timeline
  },
  [RESUMED_SERVICES]: {
    displayName: 'Resumed Services',
    iconClass: ICONS["trash-alt"],
    displayMessage: (event: ADTEvent, facilityName: string) => {
      return <><span className="event-text-bold">{event.status}</span> at {facilityName}</>;
    },
    display: false  // not in v1 timeline
  },
  [ENDED_SERVICES]: {
    displayName: 'Ended Services',
    iconClass: ICONS["trash-alt"],
    displayMessage: (event: ADTEvent, facilityName: string) => {
      return <><span className="event-text-bold">{event.status}</span> at {facilityName}</>;
    },
    display: false  // not in v1 timeline
  },
  [VISIT]: {
    displayName: 'Visit',
    iconClass: ICONS["clinic-medical"],
    displayMessage: (event: ADTEvent, facilityName: string) => {
      return <><span className="event-text-bold">{event.status}</span> at {facilityName}</>;
    },
    display: false  // not in v1 timeline
  },
  [NO_CURRENT_STATUS]: {
    displayName: 'No Current Status',
    iconClass: ICONS["trash-alt"],
    displayMessage: (event: ADTEvent, facilityName: string) => {
      return <><span className="event-text-bold">{event.status}</span> at {facilityName}</>;
    },
    display: false  // not in v1 timeline
  },
  [CLOSED]: {
    displayName: 'No Current Status',
    iconClass: ICONS["trash-alt"],
    displayMessage: (event: ADTEvent, facilityName: string) => {
      return <><span className="event-text-bold">{event.status}</span> at {facilityName}</>;
    },
    display: false  // not in v1 timeline
  },
};

export type GroupType = {
  displayName: string;
  iconClass: ICONS;
}

export type GroupMap = {
  [key: string]: GroupType
}

// these statuses map directly to admittingFacility types returned from pings
export const GroupTypes : GroupMap = {
  // unknown if pings does not return a status
  'Unknown': {
    displayName: 'Unknown',
    iconClass: ICONS["trash-alt"],
  },
  'HOS': {
    displayName: 'Hospital',
    iconClass: ICONS["hospital"]
  },
  'IPSYCH': {
    displayName: 'Inpatient psych',
    iconClass: ICONS["hospital"]
  },
  'REHAB_HOS': {
    displayName: 'Hospital',
    iconClass: ICONS["hospital"]
  },
  'RTC': {
    displayName: 'Residential treatment center',
    iconClass: ICONS["clinic-medical"]
  },
  'SUD': {
    displayName: 'Detox services',
    iconClass: ICONS["clinic-medical"]
  },
  'OBH': {
    displayName: 'Outpatient behavioral',
    iconClass: ICONS["clinic-medical"]
  },
  'SNF': {
    displayName: 'Post Acute',
    iconClass: ICONS["clinic-medical"]
  },
  'HHA': {
    displayName: 'Post Acute',
    iconClass: ICONS["clinic-medical"]
  },
  'LTAC': {
    displayName: 'Post Acute',
    iconClass: ICONS["clinic-medical"]
  },
  'HOSPICE': {
    displayName: 'Post Acute',
    iconClass: ICONS["clinic-medical"]
  },
  'ALF': {
    displayName: 'Post Acute',
    iconClass: ICONS["clinic-medical"]
  },
  'CCRC': {
    displayName: 'Post Acute',
    iconClass: ICONS["clinic-medical"]
  },
  'ADH': {
    displayName: 'Post Acute',
    iconClass: ICONS["clinic-medical"]
  },
  'URGENT_CARE': {
    displayName: 'Post Acute',
    iconClass: ICONS["clinic-medical"]
  },
  'PALLIATIVE': {
    displayName: 'Post Acute',
    iconClass: ICONS["clinic-medical"]
  },
  'CRISIS_SERVICES': {
    displayName: 'Crisis Services',
    iconClass: ICONS["hand-holding-medical"]
  },
  'PROMISE_SERVICES': {
    displayName: 'Promise Services',
    iconClass: ICONS["clinic-medical"]
  }
};

export const displayValueToGroupTypes = {
  'POST_ACUTE': ['SNF', 'HHA', 'LTAC', 'HOSPICE', 'ALF', 'CCRC', 'ADH', 'URGENT_CARE', 'PALLIATIVE'],
};

// Flowable is a temporary event name until we get workflows (and event types) defined by product
// https://hbuco.atlassian.net/browse/CARECO-4605
export const CareJourneyEventName: Record<CareJourneyEventType, string> = {
  [CareJourneyEventType.TCM_REFERRAL]: 'TCM',
  [CareJourneyEventType.TCM_BH_REFERRAL]: 'TCM_BH_REFERRAL',
  [CareJourneyEventType.ADT_ER_BH]: 'ADT_ER_BH',
  [CareJourneyEventType.ADT_3YR_ER_BH]: 'ADT_3YR_ER_BH',
  [CareJourneyEventType.ADT_3YR_IP_BH]: 'ADT_3YR_IP_BH',
  [CareJourneyEventType.ADT_IP_BH_PLUS_PCP]: 'ADT_IP_BH_PLUS_PCP',
  [CareJourneyEventType.ADT_NONROSTER_IP_BH]: 'ADT_NONROSTER_IP_BH',
  [CareJourneyEventType.ADT_NONROSTER_ER_BH]: 'ADT_NONROSTER_ER_BH',
  [CareJourneyEventType.FLOWABLE_WORKFLOW]: 'Flowable',
  [CareJourneyEventType.SCHEDULE_APPOINTMENT]: 'Schedule Appointment',

  // Provider-referral OE journeys show the resolved OE journey type (CARECO-6343).
  [CareJourneyEventType.PROVIDER_REQUEST_BH_ONLY]: 'BH_JOURNEY',
  [CareJourneyEventType.ED_PROVIDER_REQUEST]: 'BH_JOURNEY',
  [CareJourneyEventType.MOUD_ALERT]: 'MOUD Alert',
  [CareJourneyEventType.PDMP_REQUEST]: 'PDMP Request',

};
